
import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "api/baseurl";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { fetchSecretKey } from "encryptionAndDecryption/secretKeyDecryption";
import { decryptBody } from "encryptionAndDecryption/bodyDecryption";
import {
    Alert,
    AlertTitle,
    Stack,
    Typography,
    Box,
    CircularProgress,
    Icon,
    IconButton,
    Tooltip,
} from "@mui/material";
import { encryptBody } from "encryptionAndDecryption/bodyEncryption";
import { toast } from "react-toastify";
import MedicalAbstractBackground from "./MedicalAbstractBackground ";
import { keyframes } from "@mui/system";

function SOSNotificationPage() {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [notificationCount, setNotificationCount] = useState(0);
    const location = useLocation();
    const navigate = useNavigate();
    const highlightId = location.state?.highlightId;
    const itemRefs = useRef({});

    const loginData = JSON.parse(localStorage.getItem("loginData"));
    const userid = loginData?.userid;
    const role = loginData?.role;

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const secretKey = await fetchSecretKey();
            const token = JSON.parse(localStorage.getItem("loginData"))?.token;

            if (!token) throw new Error("Authentication token not found.");

            const response = await axios.get(`/api/notifications/notifications/visible/${userid}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.status === 200) {
                const decryptedData = decryptBody(response.data, secretKey);
                const allNotifications = decryptedData.notifications || [];
                const sosNotifications = allNotifications.filter(n =>n.type === "SOS ALERT" && String(n.ccid) === String(userid)
    );
                setNotifications(sosNotifications);
                setNotificationCount(decryptedData.unreadCount || 0);
            }
        } catch (error) {
            console.error("Error fetching notifications:", error);
        } finally {
            setLoading(false);
        }
    };

   useEffect(() => {
    if (!userid) return;

    fetchNotifications();
}, [location.key]);


    const handleAccept = async (e, id) => {
        e.stopPropagation();
        try {
            const secretKey = await fetchSecretKey();
            const payload = {
                alertId: id,
                role: role,
                userId: userid
            };
            const encryptedPayload = encryptBody(payload, secretKey);
            const token = loginData?.token;

            const response = await axios.post("/api/alert/accept", encryptedPayload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "text/plain",
                },
            });



            if (response.status === 200) {
                toast.success("Alert accepted successfully");
                fetchNotifications();
            }
        } catch (error) {
            console.error("Error accepting alert:", error);
            toast.error("Failed to accept alert");
        }
    };

    // Scroll to highlighted item when notifications are loaded
    useEffect(() => {
        if (highlightId && notifications.length > 0 && itemRefs.current[highlightId]) {
            itemRefs.current[highlightId].scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [highlightId, notifications]);

    const spinAnimation = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  `;
    const dotPositions = [
        { top: "5%", left: "50%" },
        { top: "20%", left: "80%" },
        { top: "50%", left: "95%" },
        { top: "80%", left: "80%" },
        { top: "95%", left: "50%" },
        { top: "80%", left: "20%" },
        { top: "50%", left: "5%" },
        { top: "20%", left: "20%" },
    ];

    return (
        <main>
            <MedicalAbstractBackground />
            <DashboardNavbar notificationCount={notificationCount} />

            <div className="container-xl p-10 px-11" style={{ position: "relative", left: "100px" }}>
                {/* Header similar to NotificationDetails but for SOS */}
                <a href="/#/track/sos-notifications" className="text-decoration-none">
                    <h3
                        className="text-white display-6 d-flex justify-content-center align-items-center gap-2"
                        style={{
                            textAlign: "center",
                            width: "100%",
                            margin: "0 auto",
                        }}
                    >
                        <span className="material-icons" style={{ fontSize: "40px" }}>
                            warning
                        </span>
                        SOS NOTIFICATIONS
                    </h3>
                </a>
                <hr className="mt-0 mb-5" />

                <div className="container-xl px-4" style={{ padding: "10px", maxWidth: "800px", margin: "30px auto" }}>
                    {loading ? (
                        <Box
                            sx={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                width: "100vw",
                                height: "100vh",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                backgroundColor: "rgba(255, 255, 255, 0.8)",
                                zIndex: 9999,
                            }}
                        >
                            <Box
                                sx={{
                                    position: "relative",
                                    width: 80,
                                    height: 80,
                                    animation: `${spinAnimation} 1.5s linear infinite`,
                                }}
                            >
                                {dotPositions.map((pos, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            backgroundColor: "#1A237E",
                                            position: "absolute",
                                            ...pos,
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    ) : notifications.length > 0 ? (
                        <Stack spacing={2} mt={3}>
                            {notifications.map((n, index) => {
                                // Debugging logs
                                console.log("Checking highlight:", { itemID: n.id, highlightId, match: String(n.id) === String(highlightId) });

                                const isHighlighted = highlightId && String(n.id) === String(highlightId);

                                // Reuse color logic or enforce SOS redness
                                let bgColor = "#ffebee";       // light red
                                let borderColor = "#d32f2f";
                                let textColor = "#b71c1c";

                                // If specifically highlighted, maybe add extra visual cue
                                if (isHighlighted) {
                                    borderColor = "#ff0000";
                                    bgColor = "#ffcdd2"; // slightly darker red bg
                                }

                                return (
                                    <div ref={(el) => (itemRefs.current[n.id] = el)} key={n.id}>
                                        <Alert
                                            severity="error" // SOS is usually error/critical
                                            variant="outlined"
                                            sx={{
                                                position: "relative",
                                                borderLeft: `6px solid ${borderColor}`,
                                                border: isHighlighted ? `2px solid ${borderColor}` : undefined,
                                                backgroundColor: bgColor,
                                                color: textColor,
                                                borderRadius: 2,
                                                boxShadow: isHighlighted ? "0 0 15px rgba(211, 47, 47, 0.6)" : "1px 3px 8px rgba(0,0,0,0.1)",
                                                opacity: n.read ? 0.8 : 1,
                                                transition: "all 0.3s ease",
                                                transform: isHighlighted ? "scale(1.02)" : "scale(1)"
                                            }}
                                        >
                                            <Tooltip title="Accept Alert">
                                                <IconButton
                                                    color="error"
                                                    sx={{
                                                        position: "absolute",
                                                        top: 8,
                                                        right: 8,
                                                        backgroundColor: "rgba(211, 47, 47, 0.1)",
                                                        "&:hover": {
                                                            backgroundColor: "rgba(211, 47, 47, 0.2)",
                                                        }
                                                    }}
                                                    onClick={(e) => handleAccept(e, n.sosid)}
                                                >
                                                    <Icon>call</Icon>
                                                </IconButton>
                                            </Tooltip>

                                            <AlertTitle>
                                                <strong
                                                    style={{ cursor: "pointer", textDecoration: "underline" }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        // Navigate to Patient Profile using patientid
                                                        // User requested navigate(`/ViewPatientProfile/${n.id}`), assuming n.id refers to patientId in their context, 
                                                        // but typically n.id is notification ID. Will use n.patientid if available, falling back to n.id if that's what they meant.
                                                        // However, typically ViewPatientProfile takes a patient ID.
                                                        // Based on NotificationDetails.js, patientId is n.patientid.
                                                        const pId = n.patientid || n.patientId || n.patientID;
                                                        navigate(`/ViewPatientProfile/${pId}`);
                                                    }}
                                                >
                                                    {n.patientName || "SOS Alert"}
                                                </strong>
                                            </AlertTitle>
                                            <Typography variant="body2">{n.message}</Typography>
                                            <Typography variant="caption" display="block" color="text.secondary" mt={1}>
                                                {n.timestamp}
                                            </Typography>
                                        </Alert>
                                    </div>
                                );
                            })}
                        </Stack>
                    ) : (
                        <Typography variant="body1" align="center" color="text.secondary" mt={3}>
                            No SOS notifications found.
                        </Typography>
                    )}
                </div>
            </div>
        </main>
    );
}

export default SOSNotificationPage;
